import { Box, useMediaQuery } from '@mui/material';
import StraightenOutlined from '@mui/icons-material/StraightenOutlined';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TerrainIcon from '@mui/icons-material/Terrain';
import LineStyleIcon from '@mui/icons-material/LineStyle';
import { StatsTile } from './StatsTile';
import type { RouteProfile } from '../profileSelector/RouteProfileSelector';
import { useState, useRef, useEffect, useCallback } from 'react';
import { formatDuration } from '../../utils/routeFormatters';
import './StatsBar.css';

export type StatType = 'distance' | 'duration' | 'ascend' | 'surface' | null;

interface TouchPoint {
  x: number;
  y: number;
  time: number;
}

interface StatsBarProps {
  distance: number | null;
  duration: number | null;
  ascend: number | null;
  routeProfile: RouteProfile;
  isLoading: boolean;
  activeStatType?: StatType;
  onStatClick: (statType: StatType) => void;
  routeSegments?: any[]; // Add segments for surface analysis
  isExpanded?: boolean;
  onDragChange?: (isDragging: boolean, dragOffset: number) => void;
}

export const StatsBar = ({
  distance,
  duration,
  ascend,
  isLoading,
  activeStatType,
  onStatClick,
  routeSegments,
  isExpanded = false,
  onDragChange
}: StatsBarProps) => {
  const formattedDistance = distance ? (distance / 1000).toFixed(1) : null;
  const formattedDuration = duration ? formatDuration(duration) : null;
  const formattedAscend = ascend ? Math.round(ascend) : null; // Format ascend to remove decimals
  
  // Calculate surface diversity for display
  const surfaceCount = routeSegments ? new Set(routeSegments.map(seg => seg.surface)).size : 0;
  
  const statsBarRef = useRef<HTMLDivElement>(null);
  
  // Detect landscape orientation on mobile/tablet devices
  // In landscape, check if height is constrained (mobile-like) rather than width
  const isSmallScreen = useMediaQuery('(max-width: 900px)');
  const isShortScreen = useMediaQuery('(max-height: 600px)'); // Landscape mobile typically has limited height
  const isOrientationLandscape = useMediaQuery('(orientation: landscape)');
  const isLandscape = (isSmallScreen || isShortScreen) && isOrientationLandscape;

  // Touch state for drag functionality
  const [touchStart, setTouchStart] = useState<TouchPoint | null>(null);
  const [touchEnd, setTouchEnd] = useState<TouchPoint | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  
  // Track which stat was initially touched for iOS Safari compatibility
  const [touchedStat, setTouchedStat] = useState<StatType>(null);

  const handleStatClick = (statType: StatType) => {
    if (!isLoading && (distance || duration || ascend)) {
      onStatClick(activeStatType === statType ? null : statType);
    }
  };

  const handleStatTouchStart = (e: React.TouchEvent, statType: StatType) => {
    // iOS Safari fix: prevent the parent touch handler from interfering
    // with individual stat interactions, BUT allow drag gestures to propagate
    setTouchedStat(statType);
    
    // Only stop propagation if drag functionality is disabled
    // If drag is enabled, let the main StatsBar handlers process the touch
    if (!onDragChange) {
      e.stopPropagation();
    }
  };

  const handleStatTouchEnd = (e: React.TouchEvent, statType: StatType) => {
    // For iOS compatibility: ensure touch events on individual stats work properly
    // even when parent has touch handlers

    // Only handle as stat click if no dragging occurred and drag is disabled OR drag didn't trigger
    if (!isDragging && !isLoading && (distance || duration || ascend) && touchedStat === statType) {
      // Only prevent default on touch devices to avoid interfering with mouse clicks
      const isTouchDevice = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      if (isTouchDevice) {
        // Check if event is cancelable before calling preventDefault
        if (e.cancelable) {
          e.preventDefault();
        }
        // Only stop propagation if drag functionality is disabled
        if (!onDragChange) {
          e.stopPropagation();
        }
      }
      handleStatClick(statType);
    }
    setTouchedStat(null);
  };

  const handleTouchStart = useCallback((e: TouchEvent | React.TouchEvent) => {
    // Prevent pull-to-refresh when detail panel is active
    if (onDragChange) {
      // Check if event is cancelable before calling preventDefault
      if (e.cancelable) {
        e.preventDefault();
      }
    }
    
    const touch = 'touches' in e ? e.touches[0] : (e as React.TouchEvent).touches[0];
    setTouchStart({
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    });
    setTouchEnd(null);
    setIsDragging(false);
  }, [onDragChange]);

  const handleTouchMove = useCallback((e: TouchEvent | React.TouchEvent) => {
    if (!touchStart) return;
    
    // Prevent pull-to-refresh when detail panel is active and we're dragging
    if (onDragChange) {
      // Check if event is cancelable before calling preventDefault
      if (e.cancelable) {
        e.preventDefault();
      }
    }
    
    const touch = 'touches' in e ? e.touches[0] : (e as React.TouchEvent).touches[0];
    const currentTouchEnd = {
      x: touch.clientX,
      y: touch.clientY,
      time: Date.now()
    };
    
    setTouchEnd(currentTouchEnd);
    
    // Calculate drag offset in real-time
    const deltaY = currentTouchEnd.y - touchStart.y; // Positive when dragging down
    
    // Start dragging if moved more than 5px
    if (Math.abs(deltaY) > 5) {
      setIsDragging(true);
      
      // Apply drag offset with resistance, but don't change state during drag
      let dragOffset = deltaY * 0.6;
      
      // If already expanded and trying to drag up further, apply heavy resistance/limit
      if (isExpanded && deltaY < 0) {
        // Allow only minimal upward movement with heavy resistance when already expanded
        dragOffset = Math.max(deltaY * 0.1, -20); // Max 20px upward movement with heavy resistance
      }
      
      // Notify parent of drag state change for visual feedback
      if (onDragChange) {
        onDragChange(true, dragOffset);
      }
    }
  }, [touchStart, isExpanded, onDragChange]);

  const handleTouchEnd = (e: React.TouchEvent) => {
    if (!touchStart || !touchEnd) {
      // Reset drag state
      setIsDragging(false);
      setTouchStart(null);
      setTouchEnd(null);
      
      // Notify parent that dragging has ended
      if (onDragChange) {
        onDragChange(false, 0);
      }
      return;
    }

    const deltaY = touchStart.y - touchEnd.y;

    // Notify parent that dragging has ended with final offset
    if (onDragChange && isDragging) {
      onDragChange(false, -deltaY); // Negative because we want upward drag to be negative offset
    } else if (!isDragging && Math.abs(deltaY) < 5) {
      // If it wasn't a drag, treat it as a tap - determine which stat was clicked
      const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
      const touchX = touchEnd.x - rect.left;
      const sectionWidth = rect.width / 4;
      
      let targetStat: StatType = 'distance';
      if (touchX < sectionWidth) {
        targetStat = 'distance';
      } else if (touchX < sectionWidth * 2) {
        targetStat = 'duration';
      } else if (touchX < sectionWidth * 3) {
        targetStat = 'ascend';
      } else {
        targetStat = 'surface';
      }
      
      // Only activate if we have data for this stat
      const hasData = (distance || duration || ascend) && !isLoading;
      if (hasData) {
        handleStatClick(targetStat);
      }
    }

    // Reset all drag state
    setIsDragging(false);
    setTouchStart(null);
    setTouchEnd(null);
    
    // Notify parent that dragging has ended
    if (onDragChange) {
      onDragChange(false, 0);
    }
  };

  // Attach native touch event listener with passive: false to allow preventDefault
  useEffect(() => {
    const element = statsBarRef.current;
    if (!element || !onDragChange) return;

    const handleNativeTouchStart = (e: TouchEvent) => {
      handleTouchStart(e);
    };

    const handleNativeTouchMove = (e: TouchEvent) => {
      handleTouchMove(e);
    };

    // Add listeners with passive: false to allow preventDefault
    element.addEventListener('touchstart', handleNativeTouchStart, { passive: false });
    element.addEventListener('touchmove', handleNativeTouchMove, { passive: false });

    return () => {
      element.removeEventListener('touchstart', handleNativeTouchStart);
      element.removeEventListener('touchmove', handleNativeTouchMove);
    };
  }, [onDragChange, handleTouchStart, handleTouchMove]);

  return (
    <Box
      ref={statsBarRef}
      className="stats-bar"
      onTouchEnd={onDragChange ? handleTouchEnd : undefined}
      sx={{
        cursor: activeStatType ? 'grab' : 'default',
        touchAction: onDragChange ? 'pan-y' : 'auto', // Allow vertical pan only when drag is enabled
        // Make StatsBar more compact in landscape - always 48px in landscape mode
        height: isLandscape ? '48px' : undefined,
        minHeight: isLandscape ? '48px' : undefined
      }}
    >
      <Box 
        onClick={() => {
          // Only handle click if not dragging
          if (!isDragging) {
            handleStatClick('distance');
          }
        }}
        onTouchStart={(e) => handleStatTouchStart(e, 'distance')}
        onTouchEnd={(e: React.TouchEvent) => handleStatTouchEnd(e, 'distance')}
        className={`stats-bar__stat ${isLoading || !distance ? 'stats-bar__stat--disabled' : 'stats-bar__stat--clickable'}`}
      >
        <StatsTile
          icon={StraightenOutlined}
          value={formattedDistance}
          unit="km"
          label="Distance"
          isActive={activeStatType === 'distance'}
          isLandscape={isLandscape}
        />
      </Box>
      
      <Box 
        onClick={() => {
          // Only handle click if not dragging
          if (!isDragging) {
            handleStatClick('duration');
          }
        }}
        onTouchStart={(e) => handleStatTouchStart(e, 'duration')}
        onTouchEnd={(e: React.TouchEvent) => handleStatTouchEnd(e, 'duration')}
        className={`stats-bar__stat ${isLoading || !duration ? 'stats-bar__stat--disabled' : 'stats-bar__stat--clickable'}`}
      >
        <StatsTile
          icon={AccessTimeIcon}
          value={formattedDuration}
          label="Duration"
          isActive={activeStatType === 'duration'}
          isLandscape={isLandscape}
        />
      </Box>
      
      <Box 
        onClick={() => {
          // Only handle click if not dragging
          if (!isDragging) {
            handleStatClick('ascend');
          }
        }}
        onTouchStart={(e) => handleStatTouchStart(e, 'ascend')}
        onTouchEnd={(e: React.TouchEvent) => handleStatTouchEnd(e, 'ascend')}
        className={`stats-bar__stat ${isLoading || !ascend ? 'stats-bar__stat--disabled' : 'stats-bar__stat--clickable'}`}
      >
        <StatsTile
          icon={TerrainIcon}
          value={formattedAscend}
          unit="  m"
          label="Elevation"
          isActive={activeStatType === 'ascend'}
          isLandscape={isLandscape}
        />
      </Box>
      
      <Box 
        onClick={() => {
          // Only handle click if not dragging
          if (!isDragging) {
            handleStatClick('surface');
          }
        }}
        onTouchStart={(e) => handleStatTouchStart(e, 'surface')}
        onTouchEnd={(e: React.TouchEvent) => handleStatTouchEnd(e, 'surface')}
        className={`stats-bar__stat ${isLoading || !distance ? 'stats-bar__stat--disabled' : 'stats-bar__stat--clickable'}`}
      >
        <StatsTile
          icon={LineStyleIcon}
          value={surfaceCount > 0 ? surfaceCount.toString() : null}
          unit=""
          label="Surfaces"
          isActive={activeStatType === 'surface'}
          isLandscape={isLandscape}
        />
      </Box>
    </Box>
  );
};
